// src/pages/Home.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import {
  getDashboardOverview,
  getDashboardTopSystems,
  getDashboardTimeline,
  getDashboardTopRevendas,
  listarRevendasDropdown,
} from "../services/api";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

/* =========================
   Helpers (datas + labels)
========================= */
const pad2 = (n) => String(n).padStart(2, "0");

// "YYYY-MM-DD"
const toDateInput = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// retorna [from, to] como strings YYYY-MM-DD (mês atual)
const getDefaultRangeForCurrentMonth = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = now;
  return [toDateInput(from), toDateInput(to)];
};

// ISO com hora: início do dia (local) -> UTC iso
const startOfDayISO = (dateStr) => {
  const [y, m, d] = (dateStr || "").split("-").map(Number);
  const dt = new Date(y, m - 1, d, 0, 0, 0, 0);
  return dt.toISOString();
};

// ISO com hora: fim do dia (local) -> UTC iso
const endOfDayISO = (dateStr) => {
  const [y, m, d] = (dateStr || "").split("-").map(Number);
  const dt = new Date(y, m - 1, d, 23, 59, 59, 999);
  return dt.toISOString();
};

const fmtDay = (isoOrDate) => {
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}`;
};

const fmtPeriod = (fromStr, toStr) => {
  if (!fromStr || !toStr) return "—";
  const [yf, mf, df] = fromStr.split("-");
  const [yt, mt, dt] = toStr.split("-");
  if (!yf || !mf || !df || !yt || !mt || !dt) return "—";
  return `${df}/${mf}/${yf} → ${dt}/${mt}/${yt}`;
};

/* =========================
   Cores das linhas (Recharts)
========================= */
const LINE_COLORS = {
  pdv: "#2563eb", // azul
  mobile: "#22c55e", // verde
  gestao: "#a855f7", // roxo
  zentag: "#f59e0b", // âmbar
  zenpass: "#06b6d4",
};

/* =========================
   UI Components
========================= */
const StatCard = ({ title, value, subtitle, color = "blue", onClick }) => {
  const border =
    color === "blue"
      ? "border-blue-500 dark:border-blue-400"
      : color === "green"
      ? "border-green-500 dark:border-green-400"
      : color === "red"
      ? "border-red-500 dark:border-red-400"
      : color === "yellow"
      ? "border-yellow-500 dark:border-yellow-400"
      : "border-gray-400 dark:border-slate-600";

  return (
    <div
      className={`zen-card border-l-4 ${border} p-5 cursor-pointer transition transform hover:-translate-y-0.5 hover:shadow-md`}
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h5 className="text-slate-500 dark:text-slate-300 text-sm">{title}</h5>
          <p className="text-3xl font-bold text-slate-900 dark:text-white">{value ?? "—"}</p>
          {subtitle ? (
            <p className="text-xs text-slate-400 dark:text-slate-400 mt-1">{subtitle}</p>
          ) : null}
        </div>
        <div className="text-slate-300 dark:text-slate-500">
          <ArrowUpRight size={18} />
        </div>
      </div>
    </div>
  );
};

const MiniKpi = ({ label, value, kind = "gray" }) => {
  const map = {
    gray:
      "bg-gray-100 text-gray-700 border-gray-200 " +
      "dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-800",
    green:
      "bg-green-50 text-green-700 border-green-200 " +
      "dark:bg-emerald-500/10 dark:text-emerald-200 dark:border-emerald-500/20",
    red:
      "bg-red-50 text-red-700 border-red-200 " +
      "dark:bg-red-500/10 dark:text-red-200 dark:border-red-500/20",
    blue:
      "bg-blue-50 text-blue-700 border-blue-200 " +
      "dark:bg-blue-500/10 dark:text-blue-200 dark:border-blue-500/20",
    amber:
      "bg-amber-50 text-amber-800 border-amber-200 " +
      "dark:bg-amber-500/10 dark:text-amber-200 dark:border-amber-500/20",
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${map[kind]}`}>
      {label}: <span className="ml-1">{value ?? "—"}</span>
    </span>
  );
};

const SystemCard = ({ title, ativos, bloqueados, novosPeriodo, onClick }) => {
  const total = (ativos ?? 0) + (bloqueados ?? 0);

  return (
    <div className="zen-card p-4 hover:shadow-sm transition cursor-pointer" onClick={onClick}>
      <div className="flex items-center justify-between">
        <div className="font-semibold text-slate-900 dark:text-white">{title}</div>
        <div className="text-xs text-slate-400 dark:text-slate-500">ver</div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <MiniKpi label="Ativos" value={ativos} kind="green" />
        <MiniKpi label="Bloqueados" value={bloqueados} kind="red" />
        <MiniKpi label="Novos (período)" value={novosPeriodo} kind="amber" />
        <MiniKpi label="Total" value={total} kind="gray" />
      </div>
    </div>
  );
};

// Tooltip do Recharts com tema
const ZenTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div
      className="
        rounded-xl border border-gray-200 bg-white/95 backdrop-blur px-3 py-2 shadow-md
        dark:border-slate-700 dark:bg-slate-950/80
      "
    >
      <div className="text-xs text-slate-500 dark:text-slate-300 mb-1">{label}</div>
      <div className="space-y-1">
        {payload.map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between gap-6 text-sm">
            <span className="text-slate-600 dark:text-slate-200">{p.name}</span>
            <span className="font-semibold text-slate-900 dark:text-white">{p.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Home = ({ token }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  // período (date range) — padrão: mês atual
  const [dateFrom, setDateFrom] = useState(() => getDefaultRangeForCurrentMonth()[0]);
  const [dateTo, setDateTo] = useState(() => getDefaultRangeForCurrentMonth()[1]);
  const periodoLabel = useMemo(() => fmtPeriod(dateFrom, dateTo), [dateFrom, dateTo]);

  // filtro revenda
  const [revendaId, setRevendaId] = useState(""); // "" = todas
  const [revendas, setRevendas] = useState([]);

  // dados
  const [overview, setOverview] = useState(null);
  const [topSystems, setTopSystems] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [topRevendas, setTopRevendas] = useState([]);

  const isMaster = overview?.totalRevendas != null;

  const fetchRevendas = async () => {
    try {
      const r = await listarRevendasDropdown(token);
      const data = r?.data;
      const list =
        Array.isArray(data) ? data :
        Array.isArray(data?.items) ? data.items :
        Array.isArray(data?.Items) ? data.Items :
        [];
      setRevendas(list);
    } catch (e) {
      console.error("Erro ao carregar revendas:", e);
      setRevendas([]);
    }
  };

  const fetchData = async () => {
    setErro("");
    setLoading(true);

    try {
      // validação simples: se inverter, corrige automaticamente
      const safeFrom = dateFrom && dateTo && dateFrom <= dateTo ? dateFrom : dateTo;
      const safeTo = dateFrom && dateTo && dateFrom <= dateTo ? dateTo : dateFrom;

      const params = {
        from: startOfDayISO(safeFrom),
        to: endOfDayISO(safeTo),
        revendaId: revendaId ? Number(revendaId) : undefined,
      };

      const [rOv, rTop, rTl, rTopRev] = await Promise.all([
        getDashboardOverview(token, params),
        getDashboardTopSystems(token, params),
        getDashboardTimeline(token, params),
        getDashboardTopRevendas(token, { ...params, take: 10 }).catch((e) => e),
      ]);

      const ov = rOv?.data ?? null;
      setOverview(ov);

      setTopSystems(rTop?.data?.items ?? []);

      const items = rTl?.data?.items ?? [];
      setTimeline(
        items.map((x) => ({
          ...x,
          dayLabel: fmtDay(x.day),
        }))
      );

      if (rTopRev && rTopRev?.data?.items) {
        setTopRevendas(rTopRev.data.items);
      } else {
        setTopRevendas([]);
      }
    } catch (e) {
      console.error(e);
      setErro("Falha ao carregar dashboard. Verifique a API/Token e tente novamente.");
      setOverview(null);
      setTopSystems([]);
      setTimeline([]);
      setTopRevendas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRevendas();
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [token, dateFrom, dateTo, revendaId]);

  const sistemas = overview?.sistemas || {};
  const clientesTotal = overview?.totalClientes;
  const novosClientes = overview?.novosClientesPeriodo;
  const totalRevendas = overview?.totalRevendas;

  const top1 = topSystems?.[0];

  const navSistema = (sistema) => {
    const map = {
      ZenPDV: "/licenciamento/zenpdv",
      ZenMobile: "/licenciamento/zenmobile/list",
      ZenGestao: "/licenciamento/zengestao/list",
      ZenTag: "/licenciamento/zentag/list",
      ZenPass: "/licenciamento/zenpass/list",
    };
    return map[sistema] || "/home";
  };

  return (
    <div className="p-6">
      {/* Header + filtros */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">Dashboard</h2>
          <p className="text-sm text-slate-500 dark:text-slate-300/80">
            Visão geral — período <b>{periodoLabel}</b>
            {revendaId ? <span> • Revenda <b>#{revendaId}</b></span> : null}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Date range */}
          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-500 dark:text-slate-300/80">De</div>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="zen-select"
            />
            <div className="text-xs text-slate-500 dark:text-slate-300/80">Até</div>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="zen-select"
            />
          </div>

          {/* Revenda */}
          <select
            value={revendaId}
            onChange={(e) => setRevendaId(e.target.value)}
            className="zen-select min-w-[220px]"
            title="Filtrar dashboard por revenda"
          >
            <option value="">Todas as revendas</option>
            <option value="0">MASTER</option>
            {revendas.map((r) => (
              <option key={r.id} value={String(r.id)}>
                {r.nomeFantasia || r.razaoSocial || `Revenda ${r.id}`}
              </option>
            ))}
          </select>

          {/* Preset */}
          <button
            onClick={() => {
              const [df, dt] = getDefaultRangeForCurrentMonth();
              setDateFrom(df);
              setDateTo(dt);
            }}
            className="
              px-3 py-2 rounded-lg bg-gray-100 text-gray-800 text-sm hover:bg-gray-200
              dark:bg-white/10 dark:hover:bg-white/15 dark:text-slate-100 border border-transparent dark:border-white/10
            "
            title="Voltar para o mês atual"
          >
            Mês atual
          </button>

          <button
            onClick={fetchData}
            className="
              px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800
              dark:bg-white/10 dark:hover:bg-white/15 dark:text-slate-100 border border-transparent dark:border-white/10
            "
          >
            Atualizar
          </button>

          <button
            onClick={() => navigate("/clientes/novo")}
            className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-500"
          >
            + Novo Cliente
          </button>

          <button
            onClick={() => navigate("/revendas")}
            className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-500"
          >
            Revendas
          </button>
        </div>
      </div>

      {erro ? (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 p-3 mb-6
                        dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200">
          {erro}
        </div>
      ) : null}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard
          title="Clientes (total)"
          value={loading ? "…" : clientesTotal}
          subtitle="Abrir listagem"
          color="blue"
          onClick={() => navigate(revendaId ? `/clientes?revendaId=${revendaId}` : "/clientes")}
        />

        <StatCard
          title="Novos clientes (período)"
          value={loading ? "…" : novosClientes}
          subtitle={`Período: ${periodoLabel}`}
          color="green"
          onClick={() => navigate(revendaId ? `/clientes?revendaId=${revendaId}` : "/clientes")}
        />

        <StatCard
          title="Revendas (total)"
          value={loading ? "…" : (totalRevendas ?? "—")}
          subtitle={totalRevendas == null ? "Apenas master" : "Abrir gestão"}
          color="yellow"
          onClick={() => navigate("/revendas")}
        />

        <StatCard
          title="Top sistema (ativações)"
          value={loading ? "…" : (top1 ? top1.sistema : "—")}
          subtitle={top1 ? `Ativações: ${top1.ativacoes}` : "Sem dados no período"}
          color="red"
          onClick={() => (top1 ? navigate(navSistema(top1.sistema)) : null)}
        />
      </div>

      {/* Sistemas */}
      <div className="mt-8 zen-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Ativos x Bloqueados por sistema
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-300/80">
              No escopo atual (token + filtro revenda + período).
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
          <SystemCard
            title="ZenPDV"
            ativos={sistemas?.pdv?.ativos}
            bloqueados={sistemas?.pdv?.bloqueados}
            novosPeriodo={sistemas?.pdv?.novosPeriodo}
            onClick={() => navigate("/licenciamento/zenpdv")}
          />
          <SystemCard
            title="ZenMobile"
            ativos={sistemas?.mobile?.ativos}
            bloqueados={sistemas?.mobile?.bloqueados}
            novosPeriodo={sistemas?.mobile?.novosPeriodo}
            onClick={() => navigate("/licenciamento/zenmobile/list")}
          />
          <SystemCard
            title="ZenGestão"
            ativos={sistemas?.gestao?.ativos}
            bloqueados={sistemas?.gestao?.bloqueados}
            novosPeriodo={sistemas?.gestao?.novosPeriodo}
            onClick={() => navigate("/licenciamento/zengestao/list")}
          />
          <SystemCard
            title="ZenTag"
            ativos={sistemas?.zentag?.ativos}
            bloqueados={sistemas?.zentag?.bloqueados}
            novosPeriodo={sistemas?.zentag?.novosPeriodo}
            onClick={() => navigate("/licenciamento/zentag/list")}
          />
          <SystemCard
            title="ZenPass"
            ativos={sistemas?.zenpass?.ativos}
            bloqueados={sistemas?.zenpass?.bloqueados}
            novosPeriodo={sistemas?.zenpass?.novosPeriodo}
            onClick={() => navigate("/licenciamento/zenpass/list")}
          />
        </div>
      </div>

      {/* Gráfico vivo (Timeline) */}
      <div className="mt-6 zen-card p-5">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Ativações por dia (período)
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-300/80">
              Linha viva por sistema — use o filtro de datas e revenda acima.
            </p>
          </div>

          <div className="text-xs text-slate-400 dark:text-slate-400">
            Dica: passe o mouse pra ver o tooltip
          </div>
        </div>

        <div className="h-[320px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timeline}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dayLabel" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip content={<ZenTooltip />} />
              <Legend />

              <Line
                type="monotone"
                dataKey="pdv"
                name="ZenPDV"
                stroke={LINE_COLORS.pdv}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="mobile"
                name="ZenMobile"
                stroke={LINE_COLORS.mobile}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="gestao"
                name="ZenGestão"
                stroke={LINE_COLORS.gestao}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="zentag"
                name="ZenTag"
                stroke={LINE_COLORS.zentag}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="zenpass"
                name="ZenPass"
                stroke={LINE_COLORS.zenpass}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Ranking Top Systems */}
      <div className="mt-6 zen-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Ranking de ativações (período)
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-300/80">
              Qual sistema mais vendeu no período.
            </p>
          </div>
        </div>

        <div className="overflow-x-auto border border-gray-200 rounded-xl dark:border-slate-800">
          <table className="zen-table">
            <thead className="zen-thead">
              <tr>
                <th className="zen-th">Sistema</th>
                <th className="zen-th text-right">Ativações</th>
              </tr>
            </thead>
            <tbody>
              {(topSystems || []).map((x) => (
                <tr
                  key={x.sistema}
                  className="zen-tr cursor-pointer"
                  onClick={() => navigate(navSistema(x.sistema))}
                  title="Abrir lista do sistema"
                >
                  <td className="zen-td">{x.sistema}</td>
                  <td className="zen-td text-right font-semibold">{x.ativacoes}</td>
                </tr>
              ))}
              {(!topSystems || topSystems.length === 0) && (
                <tr>
                  <td colSpan={2} className="py-6 text-center text-slate-500 dark:text-slate-300/80">
                    Sem ativações no período.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Revendas */}
      <div className="mt-6 zen-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Top Revendas (período)
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-300/80">
              Ranking por ativações somadas (PDV/Mobile/Gestão/Tag).
              {isMaster ? "" : " • (visível apenas para master)"}
            </p>
          </div>
        </div>

        <div className="overflow-x-auto border border-gray-200 rounded-xl dark:border-slate-800">
          <table className="zen-table">
            <thead className="zen-thead">
              <tr>
                <th className="zen-th">Revenda</th>
                <th className="zen-th text-right">Total</th>
                <th className="zen-th text-right">PDV</th>
                <th className="zen-th text-right">Mobile</th>
                <th className="zen-th text-right">Gestão</th>
                <th className="zen-th text-right">Tag</th>
              </tr>
            </thead>

            <tbody>
              {(topRevendas || []).map((r) => (
                <tr
                  key={r.revendaId}
                  className="zen-tr cursor-pointer"
                  title="Abrir clientes filtrados por revenda"
                  onClick={() => navigate(`/clientes?revendaId=${r.revendaId}`)}
                >
                  <td className="zen-td">
                    <div className="font-medium text-slate-900 dark:text-white">{r.nome}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">#{r.revendaId}</div>
                  </td>
                  <td className="zen-td text-right font-semibold">{r.totalAtivacoes}</td>
                  <td className="zen-td text-right">{r.sistemas?.pdv ?? 0}</td>
                  <td className="zen-td text-right">{r.sistemas?.mobile ?? 0}</td>
                  <td className="zen-td text-right">{r.sistemas?.gestao ?? 0}</td>
                  <td className="zen-td text-right">{r.sistemas?.zentag ?? 0}</td>
                </tr>
              ))}

              {(!topRevendas || topRevendas.length === 0) && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-slate-500 dark:text-slate-300/80">
                    {isMaster ? "Sem dados de revendas no período." : "Disponível apenas para master."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            className="
              px-3 py-2 rounded-lg bg-gray-100 text-gray-800 text-sm hover:bg-gray-200
              dark:bg-white/10 dark:hover:bg-white/15 dark:text-slate-100 border border-transparent dark:border-white/10
            "
            onClick={() => navigate("/revendas")}
          >
            Ver gestão de revendas
          </button>

          <button
            className="
              px-3 py-2 rounded-lg bg-gray-100 text-gray-800 text-sm hover:bg-gray-200
              dark:bg-white/10 dark:hover:bg-white/15 dark:text-slate-100 border border-transparent dark:border-white/10
            "
            onClick={() => navigate(revendaId ? `/clientes?revendaId=${revendaId}` : "/clientes")}
          >
            Ver clientes
          </button>
        </div>
      </div>
    </div>
  );
};

export default Home;
